import React, { useCallback, useEffect, useState } from "react";
import { Col, Row, Toast, ToastContainer } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useGlobal } from "../../GlobalContext";
import { canCreate, canDelete, canEdit, canView } from "../common/permissionUtils";
import { sanitizeInput } from "../common/security-utils";
import DeleteModal from "../common/delete-modal.js";
import RecordsPerPage from "../common/RecordsPerPage.jsx";
import Loader from "../common/Loader.jsx";
import SearchWithAPI from "../common/search-box-new";
import { bulkDeleteRecordsApi, CUSTOM_MODULES, exportRecordsApi, getRecordsApi, updateRecordFieldApi } from "./custom-module-api";
import { downloadCsv, formatValue, isShown, listColumns, settingsOf, singularOf, sourceUrl } from "./custom-module-utils";
import { FieldValue } from "./record-fields";
import RecordBoard from "./record-board";

import Search from "../../assets/svg/search.svg";
import Plus from "../../assets/svg/add.svg";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";
import { ReactComponent as EditRow } from "../../assets/svg/edit.svg";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";

const control ={ border: "1px solid #E5E5E5", borderRadius: "8px", height: "40px", padding: "0 12px", background: "#fff" };
const headerButton = { background: "none", border: "none", padding: 0, textAlign: "left" };

const isSet = (value) =>
  value !== "" && value !== null && value !== undefined && !(typeof value === "object" && !value.value && !value.from && !value.to);

// Filters as the API takes them: empty ones dropped, autocomplete picks sent as ids.
const filterParam = (filters) => {
  const cleaned = Object.fromEntries(
    Object.entries(filters)
      .filter(([, value]) => isSet(value))
      .map(([key, value]) => [key, value?.value ?? value])
  );
  return Object.keys(cleaned).length ? JSON.stringify(cleaned) : undefined;
};

const csvCell = (field, value) => {
  const text = formatValue(field, value);
  return text === "--" ? "" : text;
};

const CustomRecordList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();

  const { rolePermissionList } = useGlobal();
  const hasCreatePermission = canCreate(rolePermissionList, CUSTOM_MODULES);
  const hasEditPermission = canEdit(rolePermissionList, CUSTOM_MODULES);
  const hasDeletePermission = canDelete(rolePermissionList, CUSTOM_MODULES);
  const hasViewPermission = canView(rolePermissionList, CUSTOM_MODULES);

  const [module, setModule] = useState(null);
  const [records, setRecords] = useState([]);
  const [boardCounts, setBoardCounts] = useState({});
  const [listLoading, setListLoading] = useState(true);
  const [search, setSearch] = useState("");
  // A related-records "View all" link arrives with a filter already set.
  const [filters, setFilters] = useState(location.state?.filters ?? {});
  const [sort, setSort] = useState(null); // null = the module's default sort
  const [limit, setLimit] = useState(null); // null = the module's page size
  const [page, setPage] = useState(0);
  const [view, setView] = useState("list");
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selected, setSelected] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // a record id, or "bulk"
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", success: true });

  const showToast = (message, success) => setToast({ show: true, message, success });

  // Every custom module's menu lands on this same page instance, so switching
  // menus must start from a clean list. Only a slug change should reset it.
  useEffect(() => {
    setModule(null);
    setListLoading(true);
    setSearch("");
    setFilters(location.state?.filters ?? {});
    setSort(null);
    setLimit(null);
    setPage(0);
    setView("list");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const settings = settingsOf(module);
  const pageSize = limit ?? settings.list.page_size;
  const boardOn = view === "board" && !!settings.list.board_field;

  const queryParams = useCallback(
    () => ({
      search: sanitizeInput(search) || undefined,
      filters: filterParam(filters),
      sort_field: sort?.field,
      sort_dir: sort?.dir,
    }),
    [search, filters, sort]
  );

  const fetchRecords = useCallback(async () => {
    setListLoading(true);
    try {
      // ponytail: the board loads the 100 newest cards; columns show true totals.
      const paging = view === "board" ? { board: 1, limit: 100 } : { limit: limit ?? undefined, skip: page * pageSize };
      const response = await getRecordsApi(slug, { ...queryParams(), ...paging });
      if (response?.data?.success) {
        setModule(response.data.module);
        setRecords(response.data.records ?? []);
        setBoardCounts(response.data.board_counts ?? {});
        setTotalRecords(response.data.total_rec ?? 0);
        setTotalPages(response.data.total_pgs ?? 0);
        setSelected([]);
      }
    } catch (error) {
      console.error("Error fetching records:", error);
    } finally {
      setListLoading(false);
    }
  }, [slug, queryParams, view, limit, page, pageSize]);

  // Debounced so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(fetchRecords, 400);
    return () => clearTimeout(timer);
  }, [fetchRecords]);

  if (!module) {
    return listLoading ? (
      <Loader height="80vh" />
    ) : (
      <div className="fs-14 fw-500 text-grey text-center" style={{ padding: "80px 0" }}>
        This module does not exist or was deleted.
      </div>
    );
  }

  const columns = listColumns(module);
  const showCode = !!settings.code_prefix || records.some((record) => record.code);
  const selectable = hasDeletePermission;
  const gridColumns = [selectable && "44px", showCode && "140px", `repeat(${columns.length}, minmax(0, 1fr))`, "100px"]
    .filter(Boolean)
    .join(" ");
  const filterFields = settings.list.filters
    .map((key) => module.fields.find((field) => field.key === key))
    .filter((field) => field && isShown(field));
  const hasFilters = Object.values(filters).some(isSet);
  const activeSort = sort ?? { field: settings.list.sort_field, dir: settings.list.sort_dir };
  const allSelected = records.length > 0 && selected.length === records.length;

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const toggleSort = (field) => {
    setSort({ field, dir: activeSort.field === field && activeSort.dir === "asc" ? "desc" : "asc" });
    setPage(0);
  };

  const sortHeader = (field, label) => (
    <button type="button" className="fs-12 fw-600 text-ltgrey" style={headerButton} onClick={() => toggleSort(field)}>
      {label.toUpperCase()}
      {activeSort.field === field ? (activeSort.dir === "asc" ? " ▲" : " ▼") : ""}
    </button>
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportRecordsApi(slug, queryParams());
      if (!response?.data?.success) {
        showToast(response?.data?.message ?? "Unable to export", false);
        return;
      }
      const rows = response.data.records ?? [];
      const withCode = rows.some((row) => row.code);
      const fields = module.fields.filter(isShown);
      const header = [...(withCode ? ["Record No."] : []), ...fields.map((field) => field.label)];
      const body = rows.map((row) => [
        ...(withCode ? [row.code] : []),
        ...fields.map((field) => csvCell(field, row.data?.[field.key])),
      ]);
      downloadCsv(`${module.slug}-${new Date().toISOString().slice(0, 10)}.csv`, [header, ...body]);
      if (rows.length < (response.data.total_rec ?? 0)) {
        showToast(`Exported the first ${rows.length} of ${response.data.total_rec} records`, true);
      }
    } catch (error) {
      console.error("Error exporting records:", error);
      showToast("Unable to export", false);
    } finally {
      setExporting(false);
    }
  };

  // The card moves at once; the refetch settles it if the server refuses.
  const moveCard = async (record, option) => {
    const key = settings.list.board_field;
    setRecords((prev) => prev.map((item) => (item.id === record.id ? { ...item, data: { ...item.data, [key]: option || null } } : item)));
    const response = await updateRecordFieldApi(slug, record.id, key, option || null);
    if (!response?.data?.success) showToast(response?.data?.message ?? "Unable to move record", false);
    fetchRecords();
  };

  const filterControl = (field) => {
    const value = filters[field.key];
    if (field.type === "date" || field.type === "datetime") {
      return (
        <div key={field.key} className="d-flex align-items-center gap-1" style={control}>
          <span className="fs-12 fw-500 text-grey">{field.label}</span>
          <input
            type="date"
            className="fs-13"
            style={{ border: "none", outline: "none" }}
            aria-label={`${field.label} from`}
            value={value?.from ?? ""}
            onChange={(e) => setFilter(field.key, { ...value, from: e.target.value })}
          />
          <span className="text-grey">–</span>
          <input
            type="date"
            className="fs-13"
            style={{ border: "none", outline: "none" }}
            aria-label={`${field.label} to`}
            value={value?.to ?? ""}
            onChange={(e) => setFilter(field.key, { ...value, to: e.target.value })}
          />
        </div>
      );
    }
    if (field.type === "autocomplete") {
      return (
        <div key={field.key} style={{ width: "220px" }}>
          <SearchWithAPI
            apiUrl={sourceUrl(field.source) ?? ""}
            placeholder={`All ${field.label}`}
            selectedOption={value?.value ? value : null}
            onDropdownChange={(option) => setFilter(field.key, { value: option.value, label: option.label })}
          />
        </div>
      );
    }
    return (
      <select key={field.key} className="fs-14 fw-500" style={control} aria-label={field.label} value={value ?? ""} onChange={(e) => setFilter(field.key, e.target.value)}>
        {field.type === "checkbox" ? (
          <>
            <option value="">{field.label}: Any</option>
            <option value="true">{field.label}: Yes</option>
            <option value="false">{field.label}: No</option>
          </>
        ) : (
          <>
            <option value="">All {field.label}</option>
            {field.options.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </>
        )}
      </select>
    );
  };

  return (
    <div style={{ padding: "16px" }}>
      <ToastContainer position="top-end">
        <Toast bg={toast.success ? "success" : "danger"} show={toast.show} onClose={() => setToast({ ...toast, show: false })} delay={3000} autohide>
          <Toast.Body style={{ color: "#ffffff" }}>{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Row className="align-items-center mb-3 g-2">
        <Col sm={12} lg={4}>
          <div className="fs-20 fw-600 text-dkblue">{module.name}</div>
          <div className="fs-13 fw-500 text-grey">
            {totalRecords} records{settings.description ? ` · ${settings.description}` : ""}
          </div>
        </Col>
        <Col sm={12} lg={8}>
          <div className="d-flex align-items-center justify-content-lg-end flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2" style={control}>
              <img src={Search} alt="" style={{ width: "16px" }} />
              <input
                className="fs-14 fw-500"
                style={{ border: "none", outline: "none", height: "38px" }}
                placeholder="Search..."
                aria-label="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </div>

            {settings.list.board_field && (
              <div className="d-flex" role="group" aria-label="View">
                {["list", "board"].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    className="fs-14 fw-600"
                    aria-pressed={view === mode}
                    style={{
                      ...control,
                      borderRadius: mode === "list" ? "8px 0 0 8px" : "0 8px 8px 0",
                      background: view === mode ? "#EEF2FF" : "#fff",
                      color: view === mode ? "#3D4AF5" : "#546088",
                    }}
                    onClick={() => setView(mode)}
                  >
                    {mode === "list" ? "List" : "Board"}
                  </button>
                ))}
              </div>
            )}

            <button type="button" className="fs-14 fw-600" style={{ ...control, color: "#3D4AF5" }} disabled={exporting} onClick={handleExport}>
              {exporting ? "Exporting..." : "Export"}
            </button>

            {selectable && selected.length > 0 && !boardOn && (
              <button type="button" className="fs-14 fw-600" style={{ ...control, color: "#D61916" }} onClick={() => setDeleteTarget("bulk")}>
                Delete ({selected.length})
              </button>
            )}

            {/* {hasCreatePermission && ( */}
              <div
                className="d-flex align-items-center gap-2"
                style={{ background: "#3D4AF5", borderRadius: "8px", height: "40px", padding: "0 16px", cursor: "pointer" }}
                onClick={() => navigate(`/custom/${slug}/create`)}
              >
                <img src={Plus} alt="" style={{ width: "14px" }} />
                <div className="fs-15 fw-600 text-white">Add {singularOf(module)}</div>
              </div>
            {/* )} */}
          </div>
        </Col>
      </Row>

      {filterFields.length > 0 && (
        <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
          {filterFields.map(filterControl)}
          {hasFilters && (
            <button type="button" className="btn btn-link p-0 fs-13" onClick={() => { setFilters({}); setPage(0); }}>
              Clear filters
            </button>
          )}
        </div>
      )}

      {boardOn ? (
        listLoading && !records.length ? (
          <Loader height="160px" />
        ) : (
          <RecordBoard
            module={module}
            records={records}
            counts={boardCounts}
            canEdit={hasEditPermission}
            onOpen={(record) => hasViewPermission && navigate(`/custom/${slug}/view/${record.id}`)}
            onMove={moveCard}
          />
        )
      ) : (
        <>
          <div style={{ borderRadius: "16px", border: "1px solid #ebebeb", backgroundColor: "#ffffff", overflowX: "auto" }}>
            <div style={{ minWidth: `${(columns.length + 2) * 140}px` }}>
              <div style={{ display: "grid", gridTemplateColumns: gridColumns, borderBottom: "1px solid #ebebeb", alignItems: "center" }}>
                {selectable && (
                  <div style={{ padding: "12px 16px" }}>
                    <input type="checkbox" aria-label="Select all on this page" checked={allSelected} onChange={() => setSelected(allSelected ? [] : records.map((record) => record.id))} />
                  </div>
                )}
                {showCode && <div style={{ padding: "12px 16px" }}>{sortHeader("code", "Record No.")}</div>}
                {columns.map((field) => (
                  <div key={field.key} style={{ padding: "12px 16px" }}>{sortHeader(field.key, field.label)}</div>
                ))}
                <div className="fs-12 fw-600 text-ltgrey" style={{ padding: "12px 16px" }}>ACTION</div>
              </div>

              {listLoading ? (
                <Loader height="160px" />
              ) : records.length === 0 ? (
                <div className="fs-14 fw-500 text-grey text-center" style={{ padding: "40px 0" }}>
                  {search || hasFilters ? "No records match your search or filters." : "No records found."}
                </div>
              ) : (
                records.map((row) => (
                  <div
                    key={row.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: gridColumns,
                      borderBottom: "1px solid #f3f3f3",
                      alignItems: "center",
                      cursor: hasViewPermission ? "pointer" : "default",
                      background: selected.includes(row.id) ? "#F7F8FF" : undefined,
                    }}
                    onClick={() => hasViewPermission && navigate(`/custom/${slug}/view/${row.id}`)}
                  >
                    {selectable && (
                      <div style={{ padding: "14px 16px" }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${row.code || row.id}`}
                          checked={selected.includes(row.id)}
                          onChange={() => setSelected((prev) => (prev.includes(row.id) ? prev.filter((id) => id !== row.id) : [...prev, row.id]))}
                        />
                      </div>
                    )}
                    {showCode && <div className="fs-14 fw-600 textblue" style={{ padding: "14px 16px" }}>{row.code || "--"}</div>}
                    {columns.map((field, index) => (
                      <div
                        key={field.key}
                        className={`fs-14 ${index === 0 && !showCode ? "fw-600 textblue" : "fw-500 text-dkblue"}`}
                        style={{ padding: "14px 16px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        <FieldValue field={field} value={row.data?.[field.key]} />
                      </div>
                    ))}
                    <div className="d-flex align-items-center gap-3" style={{ padding: "14px 16px" }} onClick={(e) => e.stopPropagation()}>
                      {hasEditPermission && <EditRow style={{ cursor: "pointer" }} onClick={() => navigate(`/custom/${slug}/edit/${row.id}`)} />}
                      {hasDeletePermission && <DeleteRow style={{ cursor: "pointer" }} onClick={() => setDeleteTarget(row.id)} />}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between mt-3">
            <RecordsPerPage
              value={pageSize}
              onChange={(value) => {
                setLimit(Number(value));
                setPage(0);
              }}
            />
            {totalPages > 1 && (
              <ReactPaginate
                previousLabel={<img src={lar} alt="prev" />}
                nextLabel={<img src={rar} alt="next" />}
                pageCount={totalPages}
                forcePage={page}
                onPageChange={({ selected: next }) => setPage(next)}
                containerClassName="pagination"
                activeClassName="active"
              />
            )}
          </div>
        </>
      )}

      {deleteTarget && (
        <DeleteModal
          deleteUrl={deleteTarget === "bulk" ? null : `custom_module/${slug}/records/${deleteTarget}`}
          bulkDeleteApi={deleteTarget === "bulk" ? (ids) => bulkDeleteRecordsApi(slug, ids) : undefined}
          bulkDeleteIds={deleteTarget === "bulk" ? selected : undefined}
          onSuccess={(result) => {
            setDeleteTarget(null);
            if (result?.message) showToast(result.message, result.success);
            fetchRecords();
          }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default CustomRecordList;
